import React from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Zoom from '@mui/material/Zoom';
import { useTheme } from '@mui/material/styles';

// eslint-disable-next-line import/no-extraneous-dependencies
import { TreeItemCheckbox, TreeItemContent, TreeItemProvider, TreeItemRoot, useTreeItem } from '@mui/x-tree-view';

// Custom components
import TimeSeriesSlider from '@/ui/Slider/TimeSeriesSlider';

export interface LayerTreeItemTimeCompanionProps {
  companionId: string;
  checked: boolean;
  onToggleChecked: (evt: React.SyntheticEvent) => void;
  side?: 'L' | 'R';
  rowChecked: boolean;
  active: boolean;
  displayTime?: string;
  ticks: number;
  value: number;
  handleValueLabel: (x: number) => string;
  handleChangeCommitted: (event: React.SyntheticEvent | Event, value: number | Array<number>) => void;
  marginRight: '20px' | '44px';
}

// Second, lightweight tree row rendered directly under a timeseries LayerTreeItem while
// compare mode is on - lets the layer be shown at a second point in time on the opposite
// side of the swipe divider. Side is always mirrored from the parent row's own swipe state;
// the checkbox is the only thing the user controls directly here.
const LayerTreeItemTimeCompanion = ({
  companionId,
  checked,
  onToggleChecked,
  side,
  rowChecked,
  active,
  displayTime,
  ticks,
  value,
  handleValueLabel,
  handleChangeCommitted,
  marginRight,
}: LayerTreeItemTimeCompanionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Graying out and disabling the checkbox tracks two things about Row A, not whether this
  // checkbox itself happens to be checked: whether a side (L/R) has been picked - middle/"both"
  // means there's nothing to mirror - and whether Row A's own layer is even checked/visible.
  // Either condition failing takes the whole row out of play.
  const sideAvailable = side !== undefined;
  const rowEnabled = sideAvailable && rowChecked;

  const label = displayTime ? `${t('map:layerswiper.compare_time')} (${displayTime})` : t('map:layerswiper.compare_time');

  const sideShortLabel = side === 'L' ? t('layerswiper.left_short') : side === 'R' ? t('layerswiper.right_short') : '';
  const sideFullLabel = side === 'L' ? t('layerswiper.left') : side === 'R' ? t('layerswiper.right') : '';

  const { getRootProps, getContentProps, getCheckboxProps, getLabelProps } = useTreeItem({
    id: companionId,
    itemId: companionId,
    children: undefined,
    label,
  });

  return (
    // @ts-ignore same tree-item typing gap as LayerTreeItem
    <TreeItemProvider itemId={companionId}>
      <TreeItemRoot {...getRootProps()}>
        <TreeItemContent
          tabIndex={-1}
          sx={{
            gap: 0,
            p: 0,
            pl: 'calc(var(--TreeView-itemChildrenIndentation) * (var(--TreeView-itemDepth) + 1))',
            cursor: 'default',
            opacity: rowEnabled ? 1 : 0.5,
            '&:hover': { backgroundColor: 'transparent' },
          }}
          {...getContentProps()}
          onClick={(evt) => evt.stopPropagation()}
        >
          <TreeItemCheckbox
            {...getCheckboxProps()}
            sx={{ padding: '9px' }}
            color="primary"
            checked={checked}
            disabled={!rowEnabled}
            onClick={onToggleChecked}
          />
          <Typography
            sx={{
              width: '100%',
              // fontStyle: 'italic',
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
            }}
            component="div"
            {...getLabelProps()}
          >
            {label}
          </Typography>
          <TimeSeriesSlider
            ticks={ticks}
            value={value}
            checked={active}
            handleValueLabel={handleValueLabel}
            handleChangeCommited={handleChangeCommitted}
            valueLabelPlacement="bottom"
          />
          <Box sx={{ width: '60px', marginRight, display: 'flex', justifyContent: 'center' }}>
            {side && (
              <Tooltip
                title={sideFullLabel}
                arrow
                placement="bottom"
                slots={{ transition: Zoom }}
                slotProps={{
                  transition: { timeout: theme.transitions.duration.shortest },
                  tooltip: {
                    sx: {
                      typography: 'body2',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      bgcolor: 'grey.600',
                      borderRadius: '2px',
                      padding: '0.25rem 0.75rem',
                      transformOrigin: 'top center',
                    },
                  },
                  arrow: {
                    sx: { color: 'grey.600' },
                  },
                }}
              >
                <Box
                  sx={{
                    height: 15,
                    width: 15,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    lineHeight: '15px',
                    bgcolor: active ? 'secondary.main' : 'action.disabledBackground',
                    color: active ? 'secondary.contrastText' : 'action.disabled',
                    boxShadow: '0 0 2px 0px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {sideShortLabel}
                </Box>
              </Tooltip>
            )}
          </Box>
        </TreeItemContent>
      </TreeItemRoot>
    </TreeItemProvider>
  );
};

export default LayerTreeItemTimeCompanion;
